import { Map } from 'immutable';

import { formatAmount, formatCount } from '../utils/affiliates';
import { createController } from '../utils/controller';
import { keysToCamelCase } from '../utils/string';
import { getSize } from '../utils/iterable/getSize';
import api from '../utils/api';

import { MESSAGE, NOTIFICATION } from '../constants/notifications';
import { AFFILIATE_STATUS } from '../constants/user';
import { APPLY_STATUS } from '../constants/affiliates';
import { EMPTY } from '../constants';
import { MODALS } from '../constants/modal';

import actions from '../actions';

const historyController = createController();
const linkController = createController();
const payoutController = createController();
const payoutMethodController = createController();

const EMPTY_STATE = Map({
  link: '',
  history: EMPTY.LIST,
  payout: Map({
    availablePayoutAmount: 0,
    milestones: EMPTY.LIST,
    totalPayoutAmount: 0,
  }),
});

function abort(controller) {
  if (controller.isActive()) {
    controller.stop();
  }
}

function abortAll() {
  [
    historyController,
    linkController,
    payoutController,
    payoutMethodController,
  ].forEach(abort);
}

function* bootstrap(state) {
  abortAll();

  switch (state.getIn(['user', AFFILIATE_STATUS])) {
    case APPLY_STATUS.ACTIVE:
    case APPLY_STATUS.SUBMITTED: {
      yield function effect(dispatch) {
        dispatch(actions.affiliate.getHistory());
        dispatch(actions.affiliate.getLink());
        dispatch(actions.affiliate.getPayout());
        dispatch(actions.affiliate.getPayoutMethod());
      };

      return state;
    }

    case APPLY_STATUS.REJECTED: {
      return state.set('affiliate', EMPTY_STATE.set('modal', Map({ type: MODALS.AFFILIATE.SURVEY.SUCCESS })));
    }

    default: {
      return state.set('affiliate', EMPTY_STATE.set('modal', Map({ type: MODALS.AFFILIATE.SURVEY.START })));
    }
  }
}

function* cleanUp(state) {
  abortAll();
  return state.set('affiliate', EMPTY.MAP);
}

function* getHistory(state) {
  yield function effect(dispatch) {
    const { signal } = historyController.start();

    function onSuccess(response) {
      historyController.stop();
      dispatch(actions.affiliate.getHistory.fulfilled(response));
    }

    function onFail(error) {
      if (signal.aborted) return;

      historyController.stop();
      dispatch(actions.affiliate.getHistory.rejected(error));
    }

    api.affiliates.getHistory({ signal })
      .then(onSuccess)
      .catch(onFail);
  };

  return state;
}

function* getHistoryFulfilled(state, response) {
  const formatters = {
    amount(value) {
      return value && formatAmount(value);
    },
    milestone(value) {
      return value && formatCount(value);
    },
  };

  function reduceData(history, item) {
    return history.push(keysToCamelCase({ data: item, formatters, toImmutable: true }));
  }

  return state.setIn(['affiliate', 'history'], response.data.reduce(reduceData, EMPTY.LIST));
}

function* getHistoryRejected(state, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return state;
}

function* getLink(state) {
  yield function effect(dispatch) {
    const { signal } = linkController.start();

    function onSuccess(response) {
      linkController.stop();
      dispatch(actions.affiliate.getLink.fulfilled(response));
    }

    function onFail(error) {
      if (signal.aborted) return;

      linkController.stop();
      dispatch(actions.affiliate.getLink.rejected(error));
    }

    api.affiliates.getLink({ signal })
      .then(onSuccess)
      .catch(onFail);
  };

  return state;
}

function* getLinkFulfilled(state, response) {
  return state.setIn(['affiliate', 'link'], response.referral_link);
}

function* getLinkRejected(state, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return state;
}

function* getPayout(state) {
  yield function effect(dispatch) {
    const { signal } = payoutController.start();

    function onSuccess(response) {
      payoutController.stop();
      dispatch(actions.affiliate.getPayout.fulfilled(response));
    }

    function onFail(error) {
      if (signal.aborted) return;

      payoutController.stop();
      dispatch(actions.affiliate.getPayout.rejected(error));
    }

    api.affiliates.getPayout({ signal })
      .then(onSuccess)
      .catch(onFail);
  };

  return state;
}

function* getPayoutFulfilled(state, response) {
  const formatters = {
    available_payout_amount: formatAmount,
    total_payout_amount: formatAmount,
    total_sign_ups: formatCount,
    total_upgraded: formatCount,
  };

  return state.setIn(['affiliate', 'payout'], keysToCamelCase({ data: response, formatters, toImmutable: true }));
}

function* getPayoutRejected(state, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return state;
}

function* getPayoutMethod(state) {
  yield function effect(dispatch) {
    const { signal } = payoutMethodController.start();

    function onSuccess(response) {
      payoutMethodController.stop();
      dispatch(actions.affiliate.getPayoutMethod.fulfilled(response));
    }

    function onFail(error) {
      if (signal.aborted) return;

      payoutMethodController.stop();
      dispatch(actions.affiliate.getPayoutMethod.rejected(error));
    }

    api.affiliates.getPayoutMethod({ signal })
      .then(onSuccess)
      .catch(onFail);
  };

  return state.setIn(['affiliate', 'payoutMethodLoading'], true);
}

function* getPayoutMethodFulfilled(state, response) {
  if (state.getIn(['affiliate', 'payoutMethodLoading'])) {
    if (response?.payment_methods?.paypal_handle) {
      return state
        .deleteIn(['affiliate', 'payoutMethodLoading'])
        .setIn(['affiliate', 'payoutMethod'], response.payment_methods.paypal_handle);
    }

    return state.deleteIn(['affiliate', 'payoutMethodLoading']);
  }

  return state;
}

function* getPayoutMethodRejected(state, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  if (state.getIn(['affiliate', 'payoutMethodLoading'])) {
    return state.deleteIn(['affiliate', 'payoutMethodLoading']);
  }

  return state;
}

function* sendApplicationSurvey(state, formData) {
  yield function effect(dispatch) {
    function onSuccess() {
      dispatch(actions.user.getUserData());
      dispatch(actions.affiliate.sendApplicationSurvey.fulfilled());
    }

    function onFail(error) {
      dispatch(actions.affiliate.sendApplicationSurvey.rejected(error));
    }

    api.affiliates.sendApplicationSurvey({ formData })
      .then(onSuccess)
      .catch(onFail);
  };

  return state
    .deleteIn(['affiliate', 'modal', 'error'])
    .setIn(['affiliate', 'modal', 'processing'], true);
}

function* sendApplicationSurveyFulfilled(state) {
  if (state.getIn(['affiliate', 'modal', 'type']) === MODALS.AFFILIATE.SURVEY.APPLICATION) {
    yield function effect(dispatch) {
      dispatch(actions.affiliate.getHistory());
      dispatch(actions.affiliate.getLink());
      dispatch(actions.affiliate.getPayout());
      dispatch(actions.affiliate.getPayoutMethod());
    };

    return state.setIn(['affiliate', 'modal'], Map({ type: MODALS.AFFILIATE.SURVEY.SUCCESS }));
  }

  return state;
}

function* sendApplicationSurveyRejected(state, error) {
  if (state.getIn(['affiliate', 'modal', 'type']) === MODALS.AFFILIATE.SURVEY.APPLICATION) {
    return state
      .deleteIn(['affiliate', 'modal', 'processing'])
      .setIn(['affiliate', 'modal', 'error'], typeof error === 'string' ? error : MESSAGE.FAIL.NETWORK_ERROR);
  }

  return state;
}

function* setData(state, { path, value }) {
  const fullPath = Array.isArray(path)
    ? ['affiliate', ...path]
    : ['affiliate', path];

  if (value !== undefined) return state.setIn(fullPath, value);

  let updated = state.deleteIn(fullPath);

  fullPath.pop();

  while (getSize(fullPath) > 1) {
    if (!getSize(updated.getIn(fullPath))) {
      updated = updated.deleteIn(fullPath);
    }

    fullPath.pop();
  }

  return updated;
}

function* setPayoutMethod(state, handle) {
  yield function effect(dispatch) {
    function onSuccess() {
      dispatch(actions.affiliate.setPayoutMethod.fulfilled());
    }

    function onFail(error) {
      dispatch(actions.affiliate.setPayoutMethod.rejected(error));
    }

    api.affiliates.setPayoutMethod({ handle })
      .then(onSuccess)
      .catch(onFail);
  };

  return state
    .deleteIn(['affiliate', 'modal', 'error'])
    .setIn(['affiliate', 'modal', 'processing'], true);
}

function* setPayoutMethodFulfilled(state) {
  if (state.getIn(['affiliate', 'modal', 'type']) === MODALS.AFFILIATE.PAYOUT_METHOD) {

    yield function effect(dispatch) {
      dispatch(actions.affiliate.getPayoutMethod());
    };

    return state.deleteIn(['affiliate', 'modal']);
  }

  return state;
}

function* setPayoutMethodRejected(state, error) {
  if (state.getIn(['affiliate', 'modal', 'type']) === MODALS.AFFILIATE.PAYOUT_METHOD) {
    return state
      .deleteIn(['affiliate', 'modal', 'processing'])
      .setIn(['affiliate', 'modal', 'error'], typeof error === 'string' ? error : MESSAGE.FAIL.NETWORK_ERROR);
  }

  return state;
}

const affiliate = {
  [actions.affiliate.bootstrap]: bootstrap,
  [actions.affiliate.cleanUp]: cleanUp,
  [actions.affiliate.getHistory]: getHistory,
  [actions.affiliate.getHistory.fulfilled]: getHistoryFulfilled,
  [actions.affiliate.getHistory.rejected]: getHistoryRejected,
  [actions.affiliate.getLink]: getLink,
  [actions.affiliate.getLink.fulfilled]: getLinkFulfilled,
  [actions.affiliate.getLink.rejected]: getLinkRejected,
  [actions.affiliate.getPayout]: getPayout,
  [actions.affiliate.getPayout.fulfilled]: getPayoutFulfilled,
  [actions.affiliate.getPayout.rejected]: getPayoutRejected,
  [actions.affiliate.getPayoutMethod]: getPayoutMethod,
  [actions.affiliate.getPayoutMethod.fulfilled]: getPayoutMethodFulfilled,
  [actions.affiliate.getPayoutMethod.rejected]: getPayoutMethodRejected,
  [actions.affiliate.sendApplicationSurvey]: sendApplicationSurvey,
  [actions.affiliate.sendApplicationSurvey.fulfilled]: sendApplicationSurveyFulfilled,
  [actions.affiliate.sendApplicationSurvey.rejected]: sendApplicationSurveyRejected,
  [actions.affiliate.setData]: setData,
  [actions.affiliate.setPayoutMethod]: setPayoutMethod,
  [actions.affiliate.setPayoutMethod.fulfilled]: setPayoutMethodFulfilled,
  [actions.affiliate.setPayoutMethod.rejected]: setPayoutMethodRejected,
};

export default affiliate;
